import React, { useState } from 'react';
import {
  MoreVertical,
  Edit,
  Download,
  Trash,
  FolderIcon,
  FileIcon,
  UserPlus,
  ChevronDown,
  ChevronUp,
  Eye,
  Check,
  X,
  SlidersHorizontal,
  ArrowDown,
  ArrowUp,
  Trash2,
} from 'lucide-react';
import { Input } from '../../../../components/ui/input';

interface FileType {
  id: number;
  name: string;
  dateCreated: string;
  viewers: string[];
  status?: string;
  version?: number;
  platform?: string;
  publishDate?: string;
}

interface FolderType {
  id: string;
  name: string;
  count: number;
  files: FileType[];
}

interface EditingFolderType {
  id: string;
  name: string;
}

interface SelectedItem {
  id: string | number;
  type: 'folder' | 'file';
  name: string;
}

// Add sort type
type SortOrder = 'asc' | 'desc';

interface MobileFilesViewProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  folders: FolderType[];
  expandedFolders: { [key: string]: boolean };
  toggleFolder: (folderName: string) => void;
  sharedFiles: FileType[];
  publishedFiles: FileType[];
  handleDeleteFolder: (folderId: number) => void;
  handleDeleteBlog: (blogId: number) => void;
  handleDownloadFile: (blogId: number, fileName: string) => void;
  onShare: (blogId: number) => void;
  onEdit: (blogId: number) => void;
  handleUpdateFolder?: (folderId: string, newName: string) => void;
}

const MobileMenu = ({
  isFolder = false,
  onEdit,
  onDelete,
  onDownload,
  onShare,
  view,
}: {
  isFolder?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onDownload?: () => void;
  onShare?: () => void;
  view: 'myfiles' | 'published' | 'shared';
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);

  const handleAction = (action: () => void) => {
    // Close the menu and execute the action
    setIsOpen(false);
    action();
  };

  const renderMyFilesMenu = () => (
    <div className="p-4 space-y-4 text-text-primary bg-background">
      {onEdit && (
        <button
          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 rounded-lg"
          onClick={() => handleAction(onEdit)}
        >
          <Edit className="text-primary h-5 w-5" />
          <span>Edit</span>
        </button>
      )}
      {onDownload && (
        <button
          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 rounded-lg"
          onClick={() => handleAction(onDownload)}
        >
          <Download className="text-primary h-5 w-5" />
          <span>Print / Export</span>
        </button>
      )}
      {onShare && !isFolder && (
        <button
          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 rounded-lg"
          onClick={() => handleAction(onShare)}
        >
          <UserPlus className="text-primary h-5 w-5" />
          <span>Add viewers</span>
        </button>
      )}
      {onDelete && (
        <button
          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 rounded-lg"
          onClick={() => handleAction(onDelete)}
        >
          <Trash className="text-primary h-5 w-5" />
          <span>Delete</span>
        </button>
      )}
    </div>
  );

  const renderSharedMenu = () => (
    <div className="p-4 space-y-4 bg-background text-text-primary">
      {onEdit && (
        <button
          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 rounded-lg"
          onClick={() => handleAction(onEdit)}
        >
          <Edit className="text-primary h-5 w-5" />
          <span>Edit</span>
        </button>
      )}
      {onDownload && (
        <button
          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 rounded-lg"
          onClick={() => handleAction(onDownload)}
        >
          <Download className="text-primary h-5 w-5" />
          <span>Print / Export</span>
        </button>
      )}
      {onShare && (
        <button
          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 rounded-lg"
          onClick={() => handleAction(onShare)}
        >
          <UserPlus className="text-primary h-5 w-5" />
          <span>Add viewers</span>
        </button>
      )}
    </div>
  );

  const renderPublishedMenu = () => (
    <div className="p-4 space-y-4 bg-background text-text-primary">
      {onEdit && (
        <button
          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 rounded-lg"
          onClick={() => handleAction(onEdit)}
        >
          <Edit className="text-primary h-5 w-5" />
          <span>Edit</span>
        </button>
      )}
      {onDownload && (
        <button
          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 rounded-lg"
          onClick={() => handleAction(onDownload)}
        >
          <Download className="text-primary h-5 w-5" />
          <span>Print / Export</span>
        </button>
      )}
      {onDelete && (
        <button
          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 rounded-lg"
          onClick={() => handleAction(onDelete)}
        >
          <Trash className="text-primary h-5 w-5" />
          <span>Delete</span>
        </button>
      )}
    </div>
  );

  return (
    <>
      <button
        onClick={toggleMenu}
        className="p-2 hover:bg-secondary rounded-lg"
      >
        <MoreVertical className="text-indigo-500 h-6 w-6" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed z-30 inset-0 bg-black/20"
            onClick={toggleMenu}
          />
          <div className="fixed z-50 bottom-0 left-0 right-0 bg-background rounded-t-xl shadow-lg">
            <div className="w-12 h-1 bg-background rounded-full mx-auto my-3" />
            {view === 'shared'
              ? renderSharedMenu()
              : view === 'published'
                ? renderPublishedMenu()
                : renderMyFilesMenu()}
          </div>
        </>
      )}
    </>
  );
};

// Simple Sort Menu Component
const SortMenu = ({
  isOpen,
  onClose,
  onSortChange,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSortChange: (order: SortOrder) => void;
}) => {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed z-30 inset-0 bg-black/20" onClick={onClose} />
      <div className="fixed z-50 bottom-0 left-0 right-0 bg-background rounded-t-xl shadow-lg">
        <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto my-3" />
        <div className="p-4 space-y-4">
          <h3 className="text-lg font-medium text-text-primary mb-4">
            Sort Blogs By
          </h3>

          <button
            className="w-full px-4 py-3 flex items-center text-text-primary justify-between hover:bg-gray-100 rounded-lg"
            onClick={() => {
              onSortChange('desc');
              onClose();
            }}
          >
            <span>Date Created (Newest First)</span>
            <ArrowDown className="text-primary h-5 w-5" />
          </button>

          <button
            className="w-full px-4 py-3 text-text-primary flex items-center justify-between hover:bg-gray-100 rounded-lg"
            onClick={() => {
              onSortChange('asc');
              onClose();
            }}
          >
            <span>Date Created (Oldest First)</span>
            <ArrowUp className="text-primary h-5 w-5" />
          </button>
        </div>
      </div>
    </>
  );
};

const MobileFilesView: React.FC<MobileFilesViewProps> = ({
  activeTab,
  setActiveTab,
  folders,
  expandedFolders,
  toggleFolder,
  sharedFiles,
  publishedFiles,
  handleDeleteFolder,
  handleDeleteBlog,
  handleDownloadFile,
  onShare,
  onEdit,
  handleUpdateFolder,
}) => {
  // Function to handle direct actions without opening the menu
  const handleDirectEdit = (id: number) => {
    onEdit(id);
  };

  // Add state for editing folder
  const [editingFolder, setEditingFolder] = useState<EditingFolderType | null>(
    null
  );

  // Add sort state
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Add state for sort menu
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);

  // Add state for selected items
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);

  // Handle edit folder click
  const handleEditFolderClick = (folder: FolderType, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingFolder({ id: folder.id, name: folder.name });
  };

  // Handle cancel edit
  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingFolder(null);
  };

  // Handle save edit
  const handleSaveEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (editingFolder && handleUpdateFolder) {
      handleUpdateFolder(editingFolder.id, editingFolder.name);
      setEditingFolder(null);
    }
  };

  // Handle key down events for the input field
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && editingFolder && handleUpdateFolder) {
      handleUpdateFolder(editingFolder.id, editingFolder.name);
      setEditingFolder(null);
    } else if (e.key === 'Escape') {
      setEditingFolder(null);
    }
  };

  // Toggle item selection
  const toggleItemSelection = (item: SelectedItem, e: React.MouseEvent) => {
    e.stopPropagation();

    const isSelected = selectedItems.some(
      (selectedItem) =>
        selectedItem.id === item.id && selectedItem.type === item.type
    );

    let newSelectedItems: SelectedItem[];

    if (isSelected) {
      newSelectedItems = selectedItems.filter(
        (selectedItem) =>
          !(selectedItem.id === item.id && selectedItem.type === item.type)
      );
    } else {
      newSelectedItems = [...selectedItems, item];
    }

    setSelectedItems(newSelectedItems);
  };

  // Check if item is selected
  const isItemSelected = (id: string | number, type: 'folder' | 'file') => {
    return selectedItems.some((item) => item.id === id && item.type === type);
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;

    const folderIds: number[] = [];
    const blogIds: number[] = [];

    // Separate folder and file IDs
    selectedItems.forEach((item) => {
      if (item.type === 'folder') {
        folderIds.push(parseInt(item.id.toString()));
      } else if (item.type === 'file') {
        blogIds.push(parseInt(item.id.toString()));
      }
    });

    // Delete folders
    for (const folderId of folderIds) {
      handleDeleteFolder(folderId);
    }

    // Delete blogs
    for (const blogId of blogIds) {
      handleDeleteBlog(blogId);
    }

    // Clear selection
    setSelectedItems([]);
  };

  // Sort folders based on date created
  const sortedFolders = [...folders].sort((a, b) => {
    // Sort by date created - use the first file's date or a default
    const dateA = a.files[0]?.dateCreated
      ? new Date(a.files[0].dateCreated).getTime()
      : 0;
    const dateB = b.files[0]?.dateCreated
      ? new Date(b.files[0].dateCreated).getTime()
      : 0;
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  // Sort shared files
  const sortedSharedFiles = [...sharedFiles].sort((a, b) => {
    const dateA = new Date(a.dateCreated).getTime();
    const dateB = new Date(b.dateCreated).getTime();
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const renderMyFilesTab = () => (
    <div>
      {sortedFolders.map((folder) => (
        <div key={folder.id} className="border-b">
          <div
            className="p-4 flex items-center justify-between"
            onClick={() => toggleFolder(folder.name)}
          >
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={isItemSelected(folder.id, 'folder')}
                onChange={() => {}}
                onClick={(e) =>
                  toggleItemSelection(
                    {
                      id: folder.id,
                      type: 'folder',
                      name: folder.name,
                    },
                    e
                  )
                }
              />
              <div className="flex items-center gap-2">
                <FolderIcon size={18} className="text-[#846CEC]" />
                {editingFolder?.id === folder.id ? (
                  <div
                    className="flex items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Input
                      value={editingFolder.name}
                      onChange={(e) =>
                        setEditingFolder({
                          ...editingFolder,
                          name: e.target.value,
                        })
                      }
                      onKeyDown={handleKeyDown}
                      autoFocus
                      className="h-7 py-0 text-sm"
                    />
                    <button
                      className="p-1 text-green-500 hover:text-green-700"
                      onClick={handleSaveEdit}
                    >
                      <Check size={16} />
                    </button>
                    <button
                      className="p-1 text-red-500 hover:text-red-700"
                      onClick={handleCancelEdit}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <span className="font-medium">
                    {folder.name} ({folder.count})
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center">
              <div className="mr-2">
                {expandedFolders[folder.name] ? (
                  <ChevronDown size={18} className="text-[#846CEC]" />
                ) : (
                  <ChevronUp size={18} className="text-[#846CEC]" />
                )}
              </div>
              {!editingFolder && (
                <MobileMenu
                  isFolder={true}
                  onEdit={() =>
                    handleEditFolderClick(
                      folder,
                      new MouseEvent('click') as unknown as React.MouseEvent
                    )
                  }
                  onDelete={() => handleDeleteFolder(parseInt(folder.id))}
                  view="myfiles"
                />
              )}
            </div>
          </div>

          {expandedFolders[folder.name] &&
            folder.files.map((file) => (
              <div key={file.id} className="border-b p-4 pl-12">
                <div className="flex items-center justify-between mb-2">
                  <div
                    className="flex items-center gap-3 flex-grow"
                    onClick={() => handleDirectEdit(file.id)}
                  >
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                      checked={isItemSelected(file.id, 'file')}
                      onChange={() => {}}
                      onClick={(e) =>
                        toggleItemSelection(
                          {
                            id: file.id,
                            type: 'file',
                            name: file.name,
                          },
                          e
                        )
                      }
                    />
                    <div className="flex items-center gap-2">
                      <FileIcon size={18} className="text-gray-400" />
                      <span>{file.name}</span>
                    </div>
                  </div>
                  <MobileMenu
                    onEdit={() => onEdit(file.id)}
                    onDelete={() => handleDeleteBlog(file.id)}
                    onDownload={() => handleDownloadFile(file.id, file.name)}
                    onShare={() => onShare(file.id)}
                    view="myfiles"
                  />
                </div>
                <div className="text-sm text-gray-500 pl-8">
                  <p>Created: {file.dateCreated}</p>
                </div>
              </div>
            ))}
        </div>
      ))}
    </div>
  );

  const renderPublishedTab = () => (
    <div>
      {publishedFiles.map((file) => (
        <div key={file.id} className="border-b p-4">
          <div className="flex items-center justify-between mb-2">
            <div
              className="flex items-center gap-3 flex-grow"
              onClick={() => handleDirectEdit(file.id)}
            >
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={isItemSelected(file.id, 'file')}
                onChange={() => {}}
                onClick={(e) =>
                  toggleItemSelection(
                    {
                      id: file.id,
                      type: 'file',
                      name: file.name,
                    },
                    e
                  )
                }
              />
              <span className="font-medium">{file.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`px-2 py-1 text-xs rounded-md ${
                  file.status === 'published'
                    ? 'bg-gray-100 text-gray-600'
                    : 'bg-purple-50 text-[#846CEC]'
                }`}
              >
                {file.status === 'published' ? 'Published' : 'Scheduled'}
              </span>
              <MobileMenu
                onEdit={() => onEdit(file.id)}
                onDownload={() => handleDownloadFile(file.id, file.name)}
                onDelete={() => handleDeleteBlog(file.id)}
                view="published"
              />
            </div>
          </div>
          <div className="text-sm text-gray-500">
            <p>Version: {file.version || '01'}</p>
            <p>Platform: {file.platform || 'LinkedIn...'}</p>
            <p>Date Added: {file.dateCreated}</p>
          </div>
        </div>
      ))}
    </div>
  );

  const renderSharedTab = () => (
    <div>
      {sortedSharedFiles.length === 0 ? (
        <div className="p-8">
          <div className="text-center flex flex-col items-center justify-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
              <Eye size={24} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              No shared blogs
            </h3>
            <p className="text-gray-500">
              Blogs shared with you will appear here
            </p>
          </div>
        </div>
      ) : (
        sortedSharedFiles.map((file) => (
          <div key={file.id} className="border-b p-4">
            <div className="flex items-center justify-between mb-2">
              <div
                className="flex items-center gap-3 flex-grow"
                onClick={() => handleDirectEdit(file.id)}
              >
                <span className="font-medium">{file.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <MobileMenu
                  onEdit={() => onEdit(file.id)}
                  onDownload={() => handleDownloadFile(file.id, file.name)}
                  onShare={() => onShare(file.id)}
                  view="shared"
                />
              </div>
            </div>
            <div className="text-sm text-gray-500">
              <p>Date Added: {file.dateCreated}</p>
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="lg:hidden">
      <div className="flex border-b">
        <button
          className={`flex-1 py-4 text-center ${
            activeTab === 'myfiles'
              ? 'text-[#846CEC] border-b-2 border-[#846CEC]'
              : 'text-gray-500'
          }`}
          onClick={() => setActiveTab('myfiles')}
        >
          My Files
        </button>
        {/* <button
          className={`flex-1 py-4 text-center ${
            activeTab === 'published'
              ? 'text-[#846CEC] border-b-2 border-[#846CEC]'
              : 'text-gray-500'
          }`}
          onClick={() => setActiveTab('published')}
        >
          Published and Scheduled
        </button> */}
        <button
          className={`flex-1 py-4 text-center ${
            activeTab === 'shared'
              ? 'text-[#846CEC] border-b-2 border-[#846CEC]'
              : 'text-gray-500'
          }`}
          onClick={() => setActiveTab('shared')}
        >
          Shared with me
        </button>
      </div>

      <div className="bg-background text-text-primary">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-lg text-primary flex items-center font-semibold">
              Blog Name{' '}
              <span className="px-2 py-1 m-1 flex justify-center items-center text-xs rounded-md bg-secondary text-primary">
                <Eye size={14} /> <div className="">View Only</div>
              </span>
            </h2>
            <div className="flex items-center gap-2">
              {selectedItems.length > 0 && (
                <button
                  className="p-2 text-primary hover:bg-gray-100 rounded-full"
                  onClick={handleBulkDelete}
                  title={`Delete ${selectedItems.length} selected item(s)`}
                >
                  <Trash2 size={20} />
                  {selectedItems.length > 1 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {selectedItems.length}
                    </span>
                  )}
                </button>
              )}
              <button
                className="p-2 text-[#846CEC] hover:bg-gray-100 "
                onClick={() => setIsSortMenuOpen(true)}
              >
                <SlidersHorizontal />
              </button>
            </div>
          </div>
        </div>

        {activeTab === 'myfiles' && renderMyFilesTab()}
        {activeTab === 'published' && renderPublishedTab()}
        {activeTab === 'shared' && renderSharedTab()}
      </div>

      {/* Sort Menu */}
      <SortMenu
        isOpen={isSortMenuOpen}
        onClose={() => setIsSortMenuOpen(false)}
        onSortChange={(order) => setSortOrder(order)}
      />
    </div>
  );
};

export default MobileFilesView;
